import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { BaseBanner } from '@factorialco/f0-react/dist/experimental'
import { AcademicCap } from '@factorialco/f0-react/icons/app'
import AssignmentImage from 'assets/images/competencies/assignment.png'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { CompetenciesCompetenciesListDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useCompetenciesFeatures } from 'modules/competencies/hooks/useCompetenciesFeatures'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { CompetenciesDataCollection } from './CompetenciesDataCollection/index'
import { NewCompetencyWizard } from './NewCompetencyWizard'

const CompetenciesList = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [showNewCompetencyWizard, setShowNewCompetencyWizard] = useState(false)
  const [isBannerHidden, setIsBannerHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideCompetenciesAssignmentBanner
  )

  const { data, loading, error } = useQuery(CompetenciesCompetenciesListDocument)

  const { isJobCatalogTreeEnabled } = useCompetenciesFeatures()

  const canManageCompetencies = data?.competencies?._policies?.competenciesManage?.can ?? false
  const canImportResources = data?.universalImporter._policies.importResources.can ?? false

  const canManageJobCatalogTree = data?.jobCatalog._policies.jobCatalogTreeManage.can ?? false
  const canSeeJobCatalogTree = data?.jobCatalog._policies.jobCatalogTreeSee.can ?? false

  const canImportCompetencies =
    isJobCatalogTreeEnabled && canManageCompetencies && canImportResources

  const canBulkAssignCompetencies =
    isJobCatalogTreeEnabled && canManageJobCatalogTree && canImportResources

  const competencyGroups = data?.competencies.competencyGroups ?? []
  const jobCatalogRoles = data?.jobCatalog.roles ?? []

  const totalCompetencies = data?.competencies.competenciesConnection.totalCount ?? 0

  return (
    <ResponsivePage.Body loading={loading} error={error}>
      <Box gap="s32">
        {!isBannerHidden && canBulkAssignCompetencies && totalCompetencies > 0 && (
          <BaseBanner
            title={i18n.t('competencies_v3.competencies_bulk_assign_banner.title')}
            subtitle={i18n.t('competencies_v3.competencies_bulk_assign_banner.description')}
            mediaUrl={AssignmentImage}
            primaryAction={{
              icon: AcademicCap,
              variant: 'outline',
              label: i18n.t('competencies_v3.competencies_table.actions.assign_jobs'),
              onClick: () =>
                navigate(navigation.employees.jobCatalog.bulkAssignCompetencies.fullPath()),
            }}
            secondaryAction={{
              label: i18n.t('competencies_v3.competencies_bulk_assign_banner.go_to_job_catalog'),
              variant: 'ghost',
              onClick: () => navigate(navigation.employees.jobCatalog.fullPath()),
            }}
            onClose={() => setIsBannerHidden(true)}
          />
        )}

        <CompetenciesDataCollection
          competencyGroups={competencyGroups}
          jobCatalogRoles={jobCatalogRoles}
          canManageCompetencies={canManageCompetencies}
          canImportCompetencies={canImportCompetencies}
          canBulkAssignCompetencies={canBulkAssignCompetencies}
          canSeeJobCatalogTree={canSeeJobCatalogTree}
          onNewCompetency={() => setShowNewCompetencyWizard(true)}
        />
      </Box>

      {showNewCompetencyWizard && (
        <NewCompetencyWizard
          canManageJobCatalogTree={canManageJobCatalogTree}
          onClose={() => setShowNewCompetencyWizard(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default CompetenciesList
